
import { reactive } from 'vue'
import api from '@/services/axios'

export const themeState = reactive({
  currentTheme: 'light',
  preference: 'light'
})

let themeRequestId = 0
let mediaQuery = null
let mediaQueryHandler = null

const VALID_THEMES = ['light', 'dark', 'system']

function getSystemTheme() {
  if (typeof window === 'undefined') return 'light'

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

function removeSystemListener() {
  if (!mediaQuery || !mediaQueryHandler) return

  mediaQuery.removeEventListener('change', mediaQueryHandler)
  mediaQuery = null
  mediaQueryHandler = null
}

function applyTheme(theme) {
  const resolvedTheme =
    theme === 'system' ? getSystemTheme() : theme

  if (resolvedTheme !== 'light' && resolvedTheme !== 'dark') return

  themeState.currentTheme = resolvedTheme

  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = resolvedTheme
  }
}

function watchSystemTheme() {
  removeSystemListener()

  if (typeof window === 'undefined') return

  mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

  mediaQueryHandler = () => {
    if (themeState.preference === 'system') {
      applyTheme('system')
    }
  }

  mediaQuery.addEventListener('change', mediaQueryHandler)
}

function getToken() {
  try {
    return sessionStorage.getItem('token')
  } catch {
    return null
  }
}

function getGuestTheme() {
  try {
    const theme = localStorage.getItem('theme_guest')

    if (theme === 'light' || theme === 'dark') {
      return theme
    }
  } catch {
    // Gunakan default platform jika localStorage tidak tersedia.
  }

  return null
}

function setPreference(theme) {
  if (!VALID_THEMES.includes(theme)) return

  themeState.preference = theme

  if (theme === 'system') {
    watchSystemTheme()
  } else {
    removeSystemListener()
  }

  applyTheme(theme)
}

export async function initTheme() {
  if (typeof window === 'undefined') return

  const requestId = ++themeRequestId
  const token = getToken()

  // Akun login menggunakan preferensi tema dari backend.
  if (token) {
    // Tema sementara saat menunggu data akun.
    // Tidak menggunakan localStorage.theme karena nilainya
    // mungkin merupakan preferensi lama atau milik pengguna lain.
    setPreference('light')

    try {
      const response = await api.get('/me/theme')

      // Abaikan respons lama jika sesi sudah berubah.
      if (requestId !== themeRequestId || !getToken()) return

      const accountTheme = response.data?.theme

      if (VALID_THEMES.includes(accountTheme)) {
        setPreference(accountTheme)
      } else {
        setPreference('light')
      }
    } catch (error) {
      if (requestId === themeRequestId) {
        setPreference('light')
        console.error('Gagal memuat tema akun:', error)
      }
    }

    return
  }

  // Pengunjung yang pernah memilih tema sendiri.
  const guestTheme = getGuestTheme()

  if (guestTheme) {
    setPreference(guestTheme)
    return
  }

  // Pengunjung baru mengikuti default platform dari backend.
  try {
    const response = await api.get('/public/platform-preferences')

    if (requestId !== themeRequestId || getToken()) return

    const defaultTheme = response.data?.data?.default_theme

    setPreference(
      VALID_THEMES.includes(defaultTheme)
        ? defaultTheme
        : 'light'
    )
  } catch (error) {
    if (requestId === themeRequestId) {
      setPreference('light')
      console.error('Gagal memuat tema default platform:', error)
    }
  }
}

export function setTheme(theme) {
  if (!VALID_THEMES.includes(theme)) return

  // Batalkan permintaan initTheme yang sudah tidak relevan.
  themeRequestId++

  setPreference(theme)

  const token = getToken()

  if (!token) {
    // Simpan pilihan tema pengunjung.
    try {
      if (theme === 'light' || theme === 'dark') {
        localStorage.setItem('theme_guest', theme)
      } else {
        localStorage.removeItem('theme_guest')
      }
    } catch {
      // Tema tetap diterapkan selama halaman terbuka.
    }

    return
  }

  // Simpan tema akun ke backend.
  // Endpoint lama diasumsikan hanya menerima light/dark.
  if (theme === 'system') return

  api.patch('/me/theme', { theme }).catch(error => {
    console.error('Gagal menyimpan tema akun:', error)
  })
}
